import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import ApiService from 'services/apiService';
import { 
  EndReasonTypeStatsData, 
  EndReasonTypeFilter, 
  EndReasonTypeTotals, 
  LkEndReasonType,
  EndReasonTypeStatsResponse,
  EndReasonEditResource
} from '../types/EndReasonTypeData';

interface EndReasonTypeDataContextType {
  data: EndReasonTypeStatsData[];
  totalsByYear: EndReasonTypeTotals[];
  grandTotal: any | null;
  loading: boolean;
  error: string | null;
  
  filter: EndReasonTypeFilter;
  setFilter: (updates: Partial<EndReasonTypeFilter>) => void;
  resetFilter: () => void;
  
  activeSchoolYear: number | null;
  endReasonOptions: LkEndReasonType[];
  optionsLoading: boolean;
  optionsError: string | null;
}

const EndReasonTypeDataContext = createContext<EndReasonTypeDataContextType | undefined>(undefined);

export const EndReasonTypeDataProvider = ({ children }: { children: ReactNode }) => {
  const [filter, setFilterState] = useState<EndReasonTypeFilter>({
    schoolYears: [],
    selectedEndReasonTypes: [],
  });

  const [data, setData] = useState<EndReasonTypeStatsData[]>([]);
  const [totalsByYear, setTotalsByYear] = useState<EndReasonTypeTotals[]>([]);
  const [grandTotal, setGrandTotal] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeSchoolYear, setActiveSchoolYear] = useState<number | null>(null);
  const [endReasonOptions, setEndReasonOptions] = useState<LkEndReasonType[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOptions = async () => {
      setOptionsLoading(true);
      setOptionsError(null);
      try {
        const response = await ApiService.endReasons.getEditResource();
        const resource = response as unknown as EndReasonEditResource;
        setEndReasonOptions(resource.endReasonTypes || []);
      } catch (err) {
        console.error('Error fetching end reason options:', err);
        setOptionsError('Failed to load filter options');
      } finally {
        setOptionsLoading(false);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    let ignore = false;

    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await ApiService.studentEnrollments.getEndReasonTypeStats({ 
          schoolYears: filter.schoolYears,
          selectedEndReasonTypeIds: filter.selectedEndReasonTypes || []
        });

        if (ignore) return;

        const rawData = (response as any).activeSchoolYear !== undefined 
            ? response 
            : (response as any).data;

        const result = rawData as EndReasonTypeStatsResponse;

        setData(result.data || []);
        setTotalsByYear(result.totalsByYear || []);
        setGrandTotal(result.grandTotal || null);

        if (result.activeSchoolYear && !activeSchoolYear) {
          setActiveSchoolYear(result.activeSchoolYear);
          
          setFilterState(prev => {
            if (prev.schoolYears.length === 0) {
              return { ...prev, schoolYears: [result.activeSchoolYear] };
            }
            return prev;
          });
        }
      } catch (err) {
        if (!ignore) {
            console.error('Error fetching stats:', err);
            setError('Failed to load data');
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchStats();

    return () => {
        ignore = true;
    };
  }, [filter.schoolYears, filter.selectedEndReasonTypes]);

  const setFilter = useCallback((updates: Partial<EndReasonTypeFilter>) => {
    setFilterState(prev => ({ ...prev, ...updates }));
  }, []);

  const resetFilter = useCallback(() => {
    if (activeSchoolYear) {
      setFilterState({
        schoolYears: [activeSchoolYear],
        selectedEndReasonTypes: []
      });
    }
  }, [activeSchoolYear]);

  return (
    <EndReasonTypeDataContext.Provider
      value={{ 
        data, totalsByYear, grandTotal, loading, error,
        filter, setFilter, resetFilter,
        activeSchoolYear, endReasonOptions, optionsLoading, optionsError
      }}
    >
      {children}
    </EndReasonTypeDataContext.Provider>
  );
};

export const useEndReasonTypeData = () => {
  const context = useContext(EndReasonTypeDataContext);
  if (!context) throw new Error('useEndReasonTypeData must be used within EndReasonTypeDataProvider');
  return context;
};